import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { View, Text, ActivityIndicator, StyleSheet, ScrollView, TouchableOpacity, Platform, useWindowDimensions, StatusBar } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { isScreenshotMode } from '../../../lib/screenshotMode';
import FillBlankQuestion from '../../components/quiz/formats/FillBlankQuestion';
import MCQMultipleQuestionPremium from '../../components/quiz/formats/MCQMultipleQuestionPremium';
import MCQSingleQuestionPremium from '../../components/quiz/formats/MCQSingleQuestionPremium';
import OrderingQuestion from '../../components/quiz/formats/OrderingQuestion';
import TrueFalseQuestion from '../../components/quiz/formats/TrueFalseQuestion';
import { useFadeIn } from '../../design-system/animations';
import { useAppTheme } from '../../design-system/ThemeProvider';
import { spacing, radius } from '../../design-system/tokens';
import MultiplayerWaitingForResults from '../../features/multi-player/components/MultiplayerWaitingForResults';
import { useLanguageStore } from '../../features/quiz-core/languageStore';
import { t } from '../../i18n/translations';
import { MCQMultipleFormatData, TrueFalseAnswer, FillBlankAnswer, OrderingAnswer } from '../../types/questionFormats';
import { analyticsService } from '../services/analyticsService';

import { IQESSceneBackdrop } from './IQESComponents';
import { inferIQESSceneKey } from './iqesSceneSystem';
import { playIQESAudioCue } from './iqesSensoryFeedback';
import QuizNextButton from './QuizNextButton';
import useReducedMotion from './useReducedMotion';

type QuizQuestion = Record<string, unknown> & {
  id?: number | string;
  question?: string;
  question_type?: string;
  options?: unknown[];
  answer?: string | boolean;
  correct_answer?: string | boolean;
  explanation?: string;
  verse_reference?: string;
  verseReference?: string;
  question_code?: string;
  category_id?: number;
  difficulty_id?: number;
  min_selections?: number;
  max_selections?: number;
  partial_credit?: boolean;
  blanks?: unknown[];
  case_sensitive?: boolean;
  allow_partial_match?: boolean;
  items?: Array<{ id: number; [key: string]: unknown }>;
  instruction?: string;
  strict_ordering?: boolean;
  format_data?: Record<string, unknown> & {
    explanation?: string;
    explanation_true?: string;
    explanation_false?: string;
    options?: unknown[];
    min_selections?: number;
    max_selections?: number;
    partial_credit?: boolean;
    correct_answer?: boolean;
    blanks?: unknown[];
    case_sensitive?: boolean;
    allow_partial_match?: boolean;
    items?: Array<{ id: number; [key: string]: unknown }>;
    instruction?: string;
    strict_ordering?: boolean;
    screenshot_phase?: 'entrance' | 'selected' | 'typing' | 'dragging' | 'locked' | 'feedback' | 'verse';
  };
};

type ThemeColors = ReturnType<typeof useAppTheme>['colors'];

interface UnifiedQuizRendererProps {
  // Mode detection
  mode: 'solo' | 'multiplayer';

  // Shared quiz state
  isLoading: boolean;
  isGameFinished: boolean;
  error: string | null;
  questions: QuizQuestion[];
  currentQuestion: QuizQuestion | undefined;
  selectedAnswer: string | number[] | Record<string, string> | null;
  showExplanation: boolean;
  currentIndex: number;
  totalQuestions: number;
  score: number;
  correctCount?: number;
  timeLeft: number;
  maxTime: number;

  // Shared callbacks
  onAnswerSelect: (answer: unknown) => void;

  // Solo-specific props
  onNextQuestion?: () => void; // Solo only - immediate progression
  onRetryLoadQuestions?: () => void; // Solo retry callback

  // Multiplayer-specific props
  isLoadingSessionData?: boolean; // Multiplayer loading state
  isNavigating?: boolean; // Multiplayer navigation state
  sessionError?: string | null; // Multiplayer session errors
  onRetryLoadSession?: () => void; // Multiplayer retry callback
  roomLanguage?: string | null; // Multiplayer room language
  languageMismatch?: boolean; // Multiplayer language mismatch
  appLanguage?: string; // Multiplayer app language
  gracePeriodEnd?: string | null; // Multiplayer grace period
  isSubmittingResults?: boolean; // Multiplayer result submission
  isReviewMode?: boolean; // Multiplayer review mode
  isWaitingForResults?: boolean; // Multiplayer waiting state
  onReviewAnswers?: () => void; // Multiplayer review callback
  onGoHome?: () => void; // Multiplayer home callback
  roomId?: string; // Multiplayer room ID
  isPlayerAnswered?: boolean; // Multiplayer player state
  onAutoSubmit?: () => void; // Multiplayer auto-submit
  isHost?: boolean; // Multiplayer host authority for waiting state
  initialSoloRevealStage?: 'feedback' | 'verse'; // Screenshot/proof harness can open the verse stage directly
  familyId?: string;
  familyName?: string;
  friendsGroupId?: string;
  friendsGroupName?: string;
}

const UnifiedQuizRenderer: React.FC<UnifiedQuizRendererProps> = ({
  mode,
  isLoading,
  isGameFinished,
  error,
  questions,
  currentQuestion,
  selectedAnswer,
  showExplanation,
  currentIndex,
  totalQuestions,
  score,
  correctCount,
  timeLeft,
  maxTime,
  onAnswerSelect,
  onNextQuestion,
  onRetryLoadQuestions,
  // Multiplayer-specific props
  isLoadingSessionData = false,
  isNavigating = false,
  sessionError = null,
  onRetryLoadSession,
  roomLanguage: _roomLanguage = null,
  languageMismatch: _languageMismatch = false,
  appLanguage: _appLanguage = 'en',
  gracePeriodEnd: _gracePeriodEnd = null,
  isSubmittingResults = false,
  isReviewMode: _isReviewMode = false,
  isWaitingForResults = false,
  onReviewAnswers: _onReviewAnswers,
  onGoHome: _onGoHome,
  roomId = '',
  isPlayerAnswered = false,
  onAutoSubmit: _onAutoSubmit,
  isHost = false,
  initialSoloRevealStage = 'feedback',
  familyId: _familyId,
  familyName: _familyName,
  friendsGroupId: _friendsGroupId,
  friendsGroupName: _friendsGroupName,
}) => {
  const { colors } = useAppTheme();
  const { height: viewportHeight } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const screenshotMode = isScreenshotMode();
  const styles = createStyles(colors);
  const { language } = useLanguageStore();
  const reduceMotion = useReducedMotion();
  const scrollViewRef = useRef<ScrollView>(null);
  const fadeStyle = useFadeIn(0, undefined, currentIndex);
  const isCompactVerseViewport = viewportHeight < 880;
  const timePercentage = (timeLeft / maxTime) * 100;
  const scoreDisplay = Math.max(0, Math.round(correctCount ?? score));
  const xpDisplay = currentQuestion?.id && Number(currentQuestion.id) >= 9100 && Number(currentQuestion.id) < 9200
    ? 1250
    : scoreDisplay * 25;
  const questionType = String(currentQuestion?.question_type || 'mcq_single');
  const screenshotPhase = currentQuestion?.format_data?.screenshot_phase;
  const hasSubmittedAnswer = selectedAnswer !== null && selectedAnswer !== undefined;
  const isFeedbackVisible = showExplanation && hasSubmittedAnswer;
  const isProofInteractionPhase = screenshotPhase === 'selected' || screenshotPhase === 'typing' || screenshotPhase === 'dragging';
  const isAnswerLocked = hasSubmittedAnswer && !showExplanation && !isProofInteractionPhase;
  const isInteractionLocked = showExplanation || isAnswerLocked || (mode === 'multiplayer' && isPlayerAnswered);
  const [soloRevealStage, setSoloRevealStage] = useState<'feedback' | 'verse'>(initialSoloRevealStage);
  const formatLabelKey = `quizFlow.format_${questionType}`;
  const formatLabel = t(formatLabelKey, language) === formatLabelKey
    ? t('quizFlow.format_mcq_single', language)
    : t(formatLabelKey, language);
  const rawCategoryLabel = String(currentQuestion?.category || currentQuestion?.category_name || '');
  const categoryLabel = rawCategoryLabel && rawCategoryLabel !== 'Kingdom Journey'
    ? rawCategoryLabel
    : t('quizFlow.kingdomJourney', language);
  const difficultyKey = Number(currentQuestion?.difficulty_id) === 3
    ? 'hard'
    : Number(currentQuestion?.difficulty_id) === 2
      ? 'medium'
      : 'easy';
  const difficultyLabel = t(`coreUI.${difficultyKey}`, language);
  const verseReference = String(
    currentQuestion?.verse_reference ||
    currentQuestion?.verseReference ||
    currentQuestion?.format_data?.verse_reference ||
    currentQuestion?.format_data?.reference ||
    ''
  );
  // SOLO/MULTIPLAYER PARITY. The answered question must render identically in both
  // modes — only the lobby (start) and results (end) may differ. This used to read
  // `soloStageActive = mode === 'solo' && isFeedbackVisible`, which removed the
  // question and options in solo and replaced them with a feedback card, so a solo
  // player never saw which option they had picked. CLAUDE.md lists "Single Play and
  // Multiplayer parity" as a guarantee; that one `mode === 'solo'` term broke it.
  //
  // The feedback card is retired rather than re-homed: every piece of it is already
  // on screen once the question stays visible. Streak and XP live in the persistent
  // header (the card hid that header and re-rendered the same values). Correct vs
  // incorrect is shown in place on the options, which is strictly better because it
  // also shows the player's own choice. The explanation renders in FULL inline via
  // DevotionalExplanation, where the card truncated it to two lines.
  //
  // The Verse Reveal stage IS kept — it is a marketed feature — but it now FOLLOWS
  // the in-place reveal instead of replacing it. Its entry point moves to the
  // progression button below.
  const soloVersePending = mode === 'solo' && isFeedbackVisible && soloRevealStage === 'feedback';
  const showSoloVerseStage = mode === 'solo' && isFeedbackVisible && soloRevealStage === 'verse';
  const showSoloFeedbackStage = false;
  const showQuestionStage = !isAnswerLocked && !showSoloVerseStage;
  const showIQESHeader = !showSoloVerseStage || isAnswerLocked;
  const displayTimeLeft = Math.min(99, Math.max(0, Math.ceil(timeLeft)));
  const [hintRevealed, setHintRevealed] = useState(false);
  const hintButtonLabel = hintRevealed
    ? t('quizFlow.hintUsed', language)
    : t('quizFlow.hint', language);
  const hintAccessibilityLabel = hintRevealed
    ? t('quizFlow.hintUsedAccessibility', language)
    : t('quizFlow.hintUseAccessibility', language);
  const displayXp = Math.max(0, xpDisplay - (hintRevealed ? 5 : 0));
  const finalQuestion = currentIndex === totalQuestions - 1;
  const soloNextLabelKey = finalQuestion ? 'quizFlow.completeJourney' : 'quizFlow.continueJourney';
  const soloNextLabel = t(soloNextLabelKey, language) === soloNextLabelKey
    ? (finalQuestion ? 'Complete Journey' : 'Continue Journey')
    : t(soloNextLabelKey, language);
  const stageSceneKey = useMemo(() => inferIQESSceneKey({
    question: String(currentQuestion?.question || ''),
    questionType,
    verseReference,
  }), [currentQuestion?.question, questionType, verseReference]);
  const verseRevealSceneKey = stageSceneKey === 'parchment' ? 'sunsetCross' : stageSceneKey;
  const lockPulse = useSharedValue(0);
  const feedbackReveal = useSharedValue(0);
  const feedbackDetailsReveal = useSharedValue(0);
  const verseReveal = useSharedValue(0);
  const verseCopyReveal = useSharedValue(0);

  useEffect(() => {
    setSoloRevealStage(initialSoloRevealStage);
    setHintRevealed(false);
  }, [currentIndex, initialSoloRevealStage, showExplanation]);

  const hintText = useMemo(() => {
    const reference = verseReference || String(currentQuestion?.format_data?.reference || '');
    if (questionType === 'fill_blank') {
      return reference ? `Read ${reference} and listen for the missing word.` : 'Read the verse slowly and listen for the missing word.';
    }
    if (questionType === 'ordering') {
      return 'Tap one answer, then tap the answer it should trade places with.';
    }
    if (questionType === 'true_false') {
      return reference ? `Think about whether ${reference} supports this statement.` : 'Ask whether the statement matches the Scripture clue.';
    }
    if (questionType === 'mcq_multiple') {
      return 'More than one answer may be right. Look for every answer that belongs.';
    }
    return reference ? `Look again at the Scripture clue from ${reference}.` : 'Look for the answer that best fits the Scripture clue.';
  }, [currentQuestion?.format_data?.reference, questionType, verseReference]);

  const handleHintPress = useCallback(() => {
    if (isInteractionLocked || hintRevealed) return;
    setHintRevealed(true);
    void playIQESAudioCue('select', { enabled: !reduceMotion });
  }, [hintRevealed, isInteractionLocked, reduceMotion]);

  useEffect(() => {
    lockPulse.value = isAnswerLocked
      ? reduceMotion
        ? withTiming(0, { duration: 80 })
        : withRepeat(
        withSequence(
          withTiming(1, { duration: 620, easing: Easing.out(Easing.cubic) }),
          withTiming(0, { duration: 620, easing: Easing.inOut(Easing.cubic) })
        ),
        -1
      )
      : withTiming(0, { duration: 180 });
  }, [isAnswerLocked, lockPulse, reduceMotion]);

  useEffect(() => {
    feedbackReveal.value = showSoloFeedbackStage
      ? reduceMotion
        ? withTiming(1, { duration: 80 })
        : withSequence(
        withTiming(0, { duration: 0 }),
        withTiming(1, { duration: 520, easing: Easing.out(Easing.cubic) })
      )
      : withTiming(0, { duration: 120 });

    feedbackDetailsReveal.value = showSoloFeedbackStage
      ? reduceMotion
        ? withTiming(1, { duration: 80 })
        : withSequence(
        withTiming(0, { duration: 0 }),
        withDelay(260, withTiming(1, { duration: 420, easing: Easing.out(Easing.cubic) }))
      )
      : withTiming(0, { duration: 100 });
  }, [showSoloFeedbackStage, feedbackReveal, feedbackDetailsReveal, reduceMotion]);

  useEffect(() => {
    verseReveal.value = showSoloVerseStage
      ? reduceMotion
        ? withTiming(1, { duration: 80 })
        : withSequence(
        withTiming(0, { duration: 0 }),
        withTiming(1, { duration: 760, easing: Easing.out(Easing.cubic) })
      )
      : withTiming(0, { duration: 120 });

    verseCopyReveal.value = showSoloVerseStage
      ? reduceMotion
        ? withTiming(1, { duration: 80 })
        : withSequence(
        withTiming(0, { duration: 0 }),
        withDelay(430, withTiming(1, { duration: 620, easing: Easing.out(Easing.cubic) }))
      )
      : withTiming(0, { duration: 100 });
  }, [showSoloVerseStage, verseReveal, verseCopyReveal, reduceMotion]);

  const lockHaloAnimatedStyle = useAnimatedStyle(() => ({
    opacity: 0.82 + lockPulse.value * 0.18,
    transform: [{ scale: 1 + lockPulse.value * 0.055 }],
  }));

  const lockVeilAnimatedStyle = useAnimatedStyle(() => ({
    opacity: 0.25 + lockPulse.value * 0.22,
  }));

  const feedbackStageAnimatedStyle = useAnimatedStyle(() => ({
    opacity: feedbackReveal.value,
    transform: [
      { translateY: (1 - feedbackReveal.value) * 18 },
      { scale: 0.975 + feedbackReveal.value * 0.025 },
    ],
  }));

  const feedbackBurstAnimatedStyle = useAnimatedStyle(() => ({
    opacity: 0.85 + feedbackReveal.value * 0.15,
    transform: [
      { scale: 0.86 + feedbackReveal.value * 0.18 },
      { rotate: `${feedbackReveal.value * 4}deg` },
    ],
  }));

  const feedbackDetailsAnimatedStyle = useAnimatedStyle(() => ({
    opacity: feedbackDetailsReveal.value,
    transform: [{ translateY: (1 - feedbackDetailsReveal.value) * 12 }],
  }));

  const verseStageAnimatedStyle = useAnimatedStyle(() => ({
    opacity: verseReveal.value,
    transform: [
      { translateY: (1 - verseReveal.value) * 22 },
      { scale: 0.97 + verseReveal.value * 0.03 },
    ],
  }));

  const verseSceneAnimatedStyle = useAnimatedStyle(() => ({
    opacity: 0.78 + verseReveal.value * 0.22,
    transform: [{ scale: 1.03 - verseReveal.value * 0.03 }],
  }));

  const verseTextAnimatedStyle = useAnimatedStyle(() => ({
    opacity: verseCopyReveal.value,
    transform: [{ translateY: (1 - verseCopyReveal.value) * 12 }],
  }));

  // Explanation dwell time tracking
  const explanationStartTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (showExplanation && mode === 'solo') {
      explanationStartTimeRef.current = Date.now();
    } else if (!showExplanation) {
      // Explanation dismissed — log dwell time
      if (explanationStartTimeRef.current) {
        const dwellMs = Date.now() - explanationStartTimeRef.current;
        analyticsService.track('EXPLANATION_DWELL_TIME', {
          dwell_time_ms: dwellMs,
          dwell_time_seconds: Math.round(dwellMs / 1000),
          question_index: currentIndex,
          question_type: currentQuestion?.question_type || 'mcq_single',
          had_explanation: !!currentQuestion?.explanation || !!currentQuestion?.format_data?.explanation,
        });
        explanationStartTimeRef.current = null;
      }
    }
  }, [showExplanation]);

  useEffect(() => {
    if (!showSoloVerseStage) {
      return;
    }

    const scrollTimer = setTimeout(() => {
      scrollViewRef.current?.scrollTo({
        y: isCompactVerseViewport ? 280 : 160,
        animated: true,
      });
    }, 120);

    return () => clearTimeout(scrollTimer);
  }, [isCompactVerseViewport, showSoloVerseStage]);

  // Helper function to get the best explanation text
  const getBestExplanation = (question: QuizQuestion, selectedAnswer: string | number[] | boolean | null): string | null => {
    // For True/False questions, prefer format-specific explanations
    if (question.question_type === 'true_false' && question.format_data && selectedAnswer !== null) {
      // Handle different answer formats - boolean, string 'true'/'false', 'True'/'False'
      let isTrue: boolean;

      if (typeof selectedAnswer === 'boolean') {
        isTrue = selectedAnswer;
      } else if (typeof selectedAnswer === 'string') {
        isTrue = selectedAnswer.toLowerCase() === 'true';
      } else {
        isTrue = false; // fallback
      }

      const formatData = question.format_data;

      if (isTrue && formatData.explanation_true) {
        return formatData.explanation_true;
      } else if (!isTrue && formatData.explanation_false) {
        return formatData.explanation_false;
      } else {
        // Log missing specific explanations for True/False questions
        const missingType = isTrue ? 'explanation_true' : 'explanation_false';
        console.warn(`[UnifiedQuizRenderer] Missing ${missingType} for True/False question ${question.id}`);
      }
    }

    // For MCQ Multiple questions, check format_data.explanation first
    if (question.question_type === 'mcq_multiple' && question.format_data?.explanation) {
      return question.format_data.explanation;
    }

    // For MCQ Single questions, check format_data.explanation first
    if (question.question_type === 'mcq_single' && question.format_data?.explanation) {
      return question.format_data.explanation;
    }

    // Only use database explanations - log if missing
    if (!question.explanation || question.explanation.trim() === '') {
      console.warn(`[UnifiedQuizRenderer] Missing explanation for ${question.question_type} question ${question.id}`);
    }
    return question.explanation || null;
  };

  const feedbackSummary = useMemo(() => {
    const options = currentQuestion?.format_data?.options || currentQuestion?.options || [];
    const answerValue = currentQuestion?.correct_answer ?? currentQuestion?.answer ?? currentQuestion?.format_data?.correct_answer;
    const selectedValue = selectedAnswer;

    const optionText = (value: unknown) => {
      const valueString = String(value);
      const option = options.find((candidate) => {
        if (candidate && typeof candidate === 'object') {
          const optionRecord = candidate as Record<string, unknown>;
          return String(optionRecord.id) === valueString || String(optionRecord.text) === valueString;
        }
        return String(candidate) === valueString;
      });

      if (option && typeof option === 'object') {
        return String((option as Record<string, unknown>).text ?? valueString);
      }

      return valueString;
    };

    const normalizeForCompare = (value: unknown) => String(value).trim().toLowerCase();

    const selectedIds = () => {
      if (Array.isArray(selectedValue)) {
        return selectedValue.map((value) => Number(value)).filter((value) => Number.isFinite(value));
      }

      if (selectedValue && typeof selectedValue === 'object') {
        const selectedRecord = selectedValue as Record<string, unknown>;
        const order = selectedRecord.order;
        if (Array.isArray(order)) {
          return order.map((value) => Number(value)).filter((value) => Number.isFinite(value));
        }
      }

      return [];
    };

    const correctMultipleOptions = options
      .filter((candidate) => Boolean((candidate as Record<string, unknown>)?.correct))
      .map((candidate) => candidate as Record<string, unknown>);

    const correctMultipleIds = correctMultipleOptions
      .map((candidate) => Number(candidate.id))
      .filter((value) => Number.isFinite(value));

    const fillBlankCorrectAnswers = (() => {
      if (currentQuestion?.question_type !== 'fill_blank') {
        return [];
      }

      const blanks = currentQuestion.format_data?.blanks || currentQuestion.blanks || [];
      return blanks
        .map((blank) => {
          if (!blank || typeof blank !== 'object') {
            return '';
          }
          return String((blank as Record<string, unknown>).answer ?? '').trim();
        })
        .filter(Boolean);
    })();

    const fillBlankSelectedAnswers = (() => {
      if (currentQuestion?.question_type !== 'fill_blank' || !selectedValue || typeof selectedValue !== 'object') {
        return [];
      }

      const selectedRecord = selectedValue as Record<string, unknown>;
      const answerRecord = selectedRecord.answers && typeof selectedRecord.answers === 'object'
        ? selectedRecord.answers as Record<string, unknown>
        : selectedRecord;
      const blanks = currentQuestion.format_data?.blanks || currentQuestion.blanks || [];

      return blanks
        .map((blank) => {
          if (!blank || typeof blank !== 'object') {
            return '';
          }
          const blankId = String((blank as Record<string, unknown>).id ?? '');
          return String(answerRecord[blankId] ?? '').trim();
        })
        .filter(Boolean);
    })();

    const orderingCorrectIds = (() => {
      if (currentQuestion?.question_type !== 'ordering') {
        return [];
      }

      const items = currentQuestion.format_data?.items || currentQuestion.items || [];
      return [...items]
        .sort((left, right) => {
          const leftPosition = Number(left.position ?? left.correct_order ?? left.order ?? left.id);
          const rightPosition = Number(right.position ?? right.correct_order ?? right.order ?? right.id);
          return leftPosition - rightPosition;
        })
        .map((item) => Number(item.id))
        .filter((value) => Number.isFinite(value));
    })();

    const selectedText = (() => {
      if (selectedValue === null || selectedValue === undefined) {
        return '';
      }

      if (typeof selectedValue === 'boolean') {
        return selectedValue ? 'True' : 'False';
      }

      if (Array.isArray(selectedValue)) {
        return selectedValue.map(optionText).join(', ');
      }

      if (currentQuestion?.question_type === 'fill_blank') {
        return fillBlankSelectedAnswers.join(', ');
      }

      if (currentQuestion?.question_type === 'ordering') {
        return selectedIds().join(', ');
      }

      if (typeof selectedValue === 'object') {
        if ('answers' in selectedValue && typeof selectedValue.answers === 'object') {
          return Object.values(selectedValue.answers as Record<string, string>).filter(Boolean).join(', ');
        }
        return Object.values(selectedValue).filter(Boolean).join(', ');
      }

      return optionText(selectedValue);
    })();

    const correctText = (() => {
      if (answerValue === null || answerValue === undefined) {
        return '';
      }

      if (typeof answerValue === 'boolean') {
        return answerValue ? 'True' : 'False';
      }

      if (currentQuestion?.question_type === 'mcq_multiple' && correctMultipleOptions.length > 0) {
        return correctMultipleOptions
          .map((option) => String(option.text ?? option.value ?? option.id))
          .join(', ');
      }

      if (currentQuestion?.question_type === 'fill_blank' && fillBlankCorrectAnswers.length > 0) {
        return fillBlankCorrectAnswers.join(', ');
      }

      if (currentQuestion?.question_type === 'ordering' && orderingCorrectIds.length > 0) {
        return orderingCorrectIds.join(', ');
      }

      return optionText(answerValue);
    })();

    const isCorrect = (() => {
      if (selectedValue === null || selectedValue === undefined) {
        return false;
      }

      if (currentQuestion?.question_type === 'mcq_multiple' && correctMultipleIds.length > 0) {
        const selected = selectedIds().sort((left, right) => left - right);
        const correct = [...correctMultipleIds].sort((left, right) => left - right);
        return selected.length === correct.length && selected.every((value, index) => value === correct[index]);
      }

      if (currentQuestion?.question_type === 'fill_blank' && fillBlankCorrectAnswers.length > 0) {
        return fillBlankCorrectAnswers.length === fillBlankSelectedAnswers.length &&
          fillBlankCorrectAnswers.every((answer, index) =>
            normalizeForCompare(answer) === normalizeForCompare(fillBlankSelectedAnswers[index])
          );
      }

      if (currentQuestion?.question_type === 'ordering' && orderingCorrectIds.length > 0) {
        const selected = selectedIds();
        return selected.length === orderingCorrectIds.length &&
          selected.every((value, index) => value === orderingCorrectIds[index]);
      }

      if (answerValue === null || answerValue === undefined) {
        return false;
      }

      if (typeof selectedValue === 'boolean' || typeof answerValue === 'boolean') {
        return String(selectedValue).toLowerCase() === String(answerValue).toLowerCase();
      }

      if (Array.isArray(selectedValue)) {
        return correctText.length > 0 && selectedText.includes(correctText);
      }

      if (typeof selectedValue === 'object') {
        return selectedText.length > 0 && correctText.length > 0 && selectedText.toLowerCase().includes(correctText.toLowerCase());
      }

      return normalizeForCompare(selectedValue) === normalizeForCompare(answerValue) ||
        normalizeForCompare(optionText(selectedValue)) === normalizeForCompare(correctText);
    })();

    return {
      selectedText,
      correctText,
      isCorrect,
    };
  }, [currentQuestion, selectedAnswer]);

  const feedbackCopy = useMemo(() => {
    const questionType = currentQuestion?.question_type;
    const isCorrect = feedbackSummary.isCorrect;
    const correctText = feedbackSummary.correctText || feedbackSummary.selectedText;

    if (!isCorrect) {
      return {
        title: t('quizFlow.incorrect', language),
        body: correctText
          ? t('quizFlow.correctAnswerWithText', language).replace('{{answer}}', correctText)
          : t('quizFlow.keepLearningTogether', language),
        icon: 'sad' as const,
      };
    }

    if (questionType === 'mcq_multiple') {
      return {
        title: t('quizFlow.correct', language),
        body: t('quizFlow.allCorrectAnswersSelected', language),
        icon: 'star' as const,
      };
    }

    if (questionType === 'fill_blank') {
      return {
        title: t('quizFlow.correct', language),
        body: t('quizFlow.verseCompletedCorrectly', language),
        icon: 'trophy' as const,
      };
    }

    if (questionType === 'ordering') {
      return {
        title: t('quizFlow.perfectOrder', language),
        body: t('quizFlow.orderedCorrectly', language),
        icon: 'trophy' as const,
      };
    }

    return {
      title: t('quizFlow.correct', language),
      body: t('quizFlow.choseRightAnswer', language),
      icon: 'trophy' as const,
    };
  }, [currentQuestion?.question_type, feedbackSummary.correctText, feedbackSummary.isCorrect, feedbackSummary.selectedText, language]);

  const explanationText = currentQuestion
    ? getBestExplanation(currentQuestion, selectedAnswer) || currentQuestion?.explanation || t('quizFlow.keepLearningTogether', language)
    : t('quizFlow.keepLearningTogether', language);

  useEffect(() => {
    if (showSoloFeedbackStage && feedbackSummary.isCorrect) {
      void playIQESAudioCue('correct', { enabled: !reduceMotion });
    }
  }, [feedbackSummary.isCorrect, reduceMotion, showSoloFeedbackStage]);

  useEffect(() => {
    if (showSoloVerseStage) {
      void playIQESAudioCue('verse', { enabled: !reduceMotion });
    }
  }, [reduceMotion, showSoloVerseStage]);

  // Multiplayer-specific: Always keep a visible waiting UI on screen.
  // This prevents a blank content area if navigation to the dedicated waiting/results
  // route does not complete cleanly before the quiz renderer exits.
  if (mode === 'multiplayer' && isWaitingForResults) {
    return (
      <MultiplayerWaitingForResults
        score={correctCount ?? score}
        totalQuestions={totalQuestions}
        onReviewAnswers={_onReviewAnswers || (() => {})}
        onGoHome={_onGoHome || (() => {})}
        roomId={roomId}
        isHost={isHost}
      />
    );
  }

  // Loading states (both modes use similar loading UI)
  if (isLoading || (mode === 'multiplayer' && (isLoadingSessionData || isNavigating))) {
    return (
      <View style={styles.container}>
        <View style={styles.centered}>
          <ActivityIndicator
            size="large"
            color={colors.primary}
          />
          <Text style={styles.loadingText}>
            {t('loading_quiz', language)}
          </Text>
        </View>
      </View>
    );
  }

  // Error states (different error handling for each mode)
  if (error || (mode === 'multiplayer' && sessionError)) {
    const displayError = error || sessionError;
    const retryHandler = mode === 'multiplayer' ? onRetryLoadSession : onRetryLoadQuestions;
    return (
      <View style={styles.container}>
        <View style={styles.centered}>
          <Ionicons name="alert-circle" size={48} color={colors.incorrect} style={{ marginBottom: spacing.lg }} />
          <Text style={styles.errorText}>
            {displayError}
          </Text>
          {retryHandler && (
            <TouchableOpacity
              style={styles.retryButton}
              onPress={retryHandler}
            >
              <Ionicons name="refresh" size={18} color={colors.white} style={{ marginRight: 6 }} />
              <Text style={styles.retryButtonText}>
                {t('common.retry', language) || 'Retry'}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  }

  // No questions available
  if (!questions || questions.length === 0) {
    const retryHandler = mode === 'multiplayer' ? onRetryLoadSession : onRetryLoadQuestions;
    return (
      <View style={styles.container}>
        <View style={styles.centered}>
          <Ionicons name="help-circle-outline" size={48} color={colors.onSurfaceVariant} style={{ marginBottom: spacing.lg }} />
          <Text style={styles.loadingText}>
            {t('noQuestionsAvailable', language)}
          </Text>
          {retryHandler && (
            <TouchableOpacity
              style={styles.retryButton}
              onPress={retryHandler}
            >
              <Ionicons name="refresh" size={18} color={colors.white} style={{ marginRight: 6 }} />
              <Text style={styles.retryButtonText}>
                {t('common.retry', language) || 'Retry'}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  }

  // Game finished state (Solo mode shows completion, Multiplayer navigates elsewhere)
  if (isGameFinished && mode === 'solo') {
    return (
      <View style={styles.container}>
        <View style={styles.centered}>
          <Ionicons name="trophy" size={64} color={colors.accent} style={{ marginBottom: spacing['2xl'] }} />
          <Text style={styles.completionTitle}>{t('quiz_complete', language)}</Text>
          <Text style={styles.scoreText}>
            {t('quizFlow.score', language) || 'Score'}: {score} / {totalQuestions}
          </Text>
          <Text style={styles.congratsText}>{t('well_done', language)}</Text>
        </View>
      </View>
    );
  }

  // Main quiz UI (shared between both modes)
  return (
    <View style={styles.container} testID="quiz-game-screen">
      <StatusBar
        hidden={screenshotMode}
        translucent
        backgroundColor="transparent"
        barStyle="light-content"
      />
      <LinearGradient
        colors={['#080B1E', '#17102E', '#2B1746']}
        style={StyleSheet.absoluteFill}
      />
      <View style={styles.ambientGlowPrimary} />
      <View style={styles.ambientGlowGold} />
      <View style={styles.starField} pointerEvents="none">
        <View style={[styles.star, styles.starOne]} />
        <View style={[styles.star, styles.starTwo]} />
        <View style={[styles.star, styles.starThree]} />
        <View style={[styles.star, styles.starFour]} />
      </View>

      <ScrollView
        ref={scrollViewRef}
        contentContainerStyle={[
          styles.scrollContent,
          screenshotMode && styles.scrollContentScreenshot,
          // Real inset in screenshot mode too: a tighter value put the HUD under the Dynamic Island
          // in store captures, which production never does.
          Platform.OS !== 'web' && { paddingTop: Math.max(insets.top, spacing.sm) },
          showSoloVerseStage && styles.scrollContentWithStickyCta,
          showSoloVerseStage && screenshotMode && styles.scrollContentWithStickyCtaScreenshot,
        ]}
        showsVerticalScrollIndicator={false}
        overScrollMode="never"
        bounces={false}
      >
        {/* Quiz Header - question and suspense stages only */}
        {showIQESHeader && (
          <View style={styles.headerContainer}>
            <View style={styles.headerCard} testID="quiz-progress-header">
              <View style={styles.headerTopRow}>
                <View style={styles.streakHudPill}>
                  <Ionicons name="flame" size={16} color="#FBBF24" />
                  <View>
                    <Text style={styles.hudMicroLabel}>{t('quizFlow.streak', language)}</Text>
                    <Text style={styles.hudValue}>x {Math.max(0, scoreDisplay)}</Text>
                  </View>
                </View>

                <View style={styles.headerMetaGroup}>
                  <Text style={styles.categoryHudText} numberOfLines={1}>{categoryLabel}</Text>
                  <Text style={styles.difficultyHudText}>{difficultyLabel}</Text>
                </View>

                <View style={styles.xpHudPill}>
                  <Ionicons name="diamond" size={16} color="#D8B4FE" />
                  <Text style={styles.xpHudText}>{displayXp}</Text>
                </View>
              </View>

              <View style={styles.metaPillRow}>
                <View style={styles.headerBadge} testID={`quiz-progress-step-${currentIndex + 1}`}>
                  <Text style={styles.headerBadgeText}>
                    {(currentIndex + 1).toString().padStart(2, '0')}/{totalQuestions.toString().padStart(2, '0')}
                  </Text>
                </View>
                <View style={styles.typeBadge}>
                  <Text style={styles.typeBadgeText}>{formatLabel}</Text>
                </View>
              </View>

            </View>

            <View style={styles.timerBar}>
              <View
                style={[
                  styles.timerFill,
                  {
                    width: `${Math.max(0, Math.min(100, timePercentage))}%`,
                    backgroundColor: timePercentage > 30 ? '#FBBF24' : timePercentage > 10 ? '#A78BFA' : '#F87171'
                  }
                ]}
              />
            </View>
          </View>
        )}


        {isAnswerLocked && (
          <View testID="iqes-answer-lock" style={styles.answerLockStage}>
            <IQESSceneBackdrop sceneKey={stageSceneKey} style={styles.lockStageBackdrop}>
              <LinearGradient
                colors={['rgba(8, 7, 18, 0.56)', 'rgba(12, 8, 28, 0.84)', 'rgba(8, 6, 18, 0.94)']}
                style={StyleSheet.absoluteFill}
              />
            </IQESSceneBackdrop>
            <Animated.View style={[styles.lockCinematicVeil, lockVeilAnimatedStyle]} />
            <View style={styles.lockParticleField} pointerEvents="none">
              <View style={[styles.lockParticle, styles.lockParticleOne]} />
              <View style={[styles.lockParticle, styles.lockParticleTwo]} />
              <View style={[styles.lockParticle, styles.lockParticleThree]} />
            </View>
            <Animated.View style={[styles.lockHalo, lockHaloAnimatedStyle]}>
              <Ionicons name="lock-closed" size={32} color="#FDE68A" />
            </Animated.View>
            <Text style={styles.stageEyebrow}>{t('quizFlow.answerSubmitted', language)}</Text>
            <Text style={styles.lockStageTitle}>{t('quizFlow.answersLocked', language)}</Text>
            <Text style={styles.lockStageBody}>{t('quizFlow.checkingAnswer', language)}</Text>
          </View>
        )}

        {/* Render question based on format type — fade in on each new question */}
        {showQuestionStage && (
        <Animated.View style={fadeStyle}>
        {currentQuestion && currentQuestion.question_type === 'mcq_single' && (
          <MCQSingleQuestionPremium
            question={currentQuestion?.question || ""}
            options={currentQuestion?.options || []}
            selectedAnswer={typeof selectedAnswer === 'string' ? selectedAnswer : undefined}
            showCorrectAnswer={showExplanation}
            onSelectAnswer={(answer: string) => {
              if (!isInteractionLocked) {
                onAnswerSelect(answer);
              }
            }}
            disabled={isInteractionLocked}
            currentIndex={currentIndex}
            totalQuestions={totalQuestions}
            correctAnswer={currentQuestion?.answer || currentQuestion?.correct_answer}
            reference={currentQuestion?.verse_reference || currentQuestion?.format_data?.reference || currentQuestion?.format_data?.verse_reference}
            explanation={showExplanation ?
              (getBestExplanation(currentQuestion, selectedAnswer) || currentQuestion?.explanation) : undefined}
          />
        )}

        {/* Render MCQ Multiple question */}
        {currentQuestion && currentQuestion.question_type === 'mcq_multiple' && (
          <MCQMultipleQuestionPremium
            question={{
              id: currentQuestion.id,
              question_code: currentQuestion.question_code || `MCQ_${currentQuestion.id}`,
              category_id: currentQuestion.category_id || 1,
              difficulty_id: currentQuestion.difficulty_id || 1,
              active: true,
              question: currentQuestion?.question || "",
              question_type: 'mcq_multiple',
              format_data: {
                question_id: currentQuestion.id,
                language_code: 'en',
                // Use original format_data.options if available (has id, text, correct structure)
                // Otherwise fall back to currentQuestion.options
                options: currentQuestion?.format_data?.options || currentQuestion?.options || [],
                min_selections: currentQuestion?.format_data?.min_selections || currentQuestion?.min_selections || 1,
                max_selections: currentQuestion?.format_data?.max_selections || currentQuestion?.max_selections || currentQuestion?.options?.length || 4,
                partial_credit: currentQuestion?.format_data?.partial_credit || currentQuestion?.partial_credit || false,
                reference: currentQuestion?.verse_reference || currentQuestion?.format_data?.reference || currentQuestion?.format_data?.verse_reference,
                screenshot_phase: currentQuestion?.format_data?.screenshot_phase,
              } as MCQMultipleFormatData
            }}
            currentIndex={currentIndex}
            totalQuestions={totalQuestions}
            onAnswer={(answer) => {
              if (!isInteractionLocked) {
                onAnswerSelect(answer);
              }
            }}
            selectedAnswers={(() => {
              if (!selectedAnswer) return [];

              // Handle array of answers (both single player and multiplayer now use arrays)
              if (Array.isArray(selectedAnswer)) {
                return selectedAnswer.map(a => typeof a === 'string' ? parseInt(a) : a).filter(a => !isNaN(a) && a != null);
              }

              // Legacy: Handle multiplayer mode where selectedAnswer might still be a comma-separated string
              if (typeof selectedAnswer === 'string' && selectedAnswer.includes(',')) {
                // Parse comma-separated string like "1,2,3" into array of numbers
                return selectedAnswer.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
              }

              // Handle single answer as array
              return [typeof selectedAnswer === 'string' ? parseInt(selectedAnswer) : selectedAnswer].filter(a => !isNaN(a) && a != null);
            })()}
            disabled={isInteractionLocked}
            showCorrectAnswer={showExplanation}
            showExplanation={showExplanation}
            isAnswered={!!selectedAnswer}
            allowPartialSubmit={true}
            explanation={(() => {
              if (!showExplanation) return undefined;
              const explanation = getBestExplanation(currentQuestion, selectedAnswer) || currentQuestion?.explanation;
              return explanation;
            })()}
          />
        )}

        {/* Render True/False question */}
        {currentQuestion && currentQuestion.question_type === 'true_false' && (
          <TrueFalseQuestion
            question={{
              ...currentQuestion,
              format_data: currentQuestion.format_data || {
                correct_answer: currentQuestion.answer === 'true' || currentQuestion.answer === true
              }
            }}
            currentIndex={currentIndex}
            totalQuestions={totalQuestions}
            selectedAnswer={selectedAnswer === 'true' ? true : selectedAnswer === 'false' ? false : undefined}
            onAnswer={(answer: TrueFalseAnswer) => {
              if (!isInteractionLocked) {
                onAnswerSelect(answer);
              }
            }}
            disabled={isInteractionLocked}
            showCorrectAnswer={showExplanation}
            highlightCorrect={showExplanation}
            showExplanation={showExplanation}
            isAnswered={!!selectedAnswer}
            theme="premium"
            showProgressIndicator={false}
            explanation={showExplanation ?
              (getBestExplanation(currentQuestion, selectedAnswer) || currentQuestion?.explanation) : undefined}
          />
        )}

        {/* Render Fill Blank question */}
        {currentQuestion && currentQuestion.question_type === 'fill_blank' && (
          <FillBlankQuestion
            question={{
              ...currentQuestion,
              format_data: currentQuestion.format_data || {
                template: currentQuestion.question || "",
                blanks: currentQuestion.blanks || [],
                case_sensitive: currentQuestion.case_sensitive || false,
                allow_partial_match: currentQuestion.allow_partial_match || true
              }
            }}
            currentIndex={currentIndex}
            totalQuestions={totalQuestions}
            onAnswer={(answer: FillBlankAnswer) => {
              if (!isInteractionLocked) {
                onAnswerSelect(answer);
              }
            }}
            answers={
              selectedAnswer && typeof selectedAnswer === 'object' && !Array.isArray(selectedAnswer)
                ? selectedAnswer
                : {}
            }
            disabled={isInteractionLocked}
            showCorrectAnswer={showExplanation}
            showExplanation={showExplanation}
            isAnswered={!!selectedAnswer}
            theme="premium"
            showProgressIndicator={false}
            explanation={showExplanation ?
              (getBestExplanation(currentQuestion, selectedAnswer) || currentQuestion?.explanation) : undefined}
          />
        )}

        {/* Render Ordering question */}
        {currentQuestion && currentQuestion.question_type === 'ordering' && (
          <OrderingQuestion
            question={{
              ...currentQuestion,
              format_data: currentQuestion.format_data || {
                items: currentQuestion.items || [],
                instruction: currentQuestion.instruction || "Put the items in the correct order",
                partial_credit: currentQuestion.partial_credit || true,
                strict_ordering: currentQuestion.strict_ordering || false
              }
            }}
            currentIndex={currentIndex}
            totalQuestions={totalQuestions}
            onAnswer={(answer: OrderingAnswer) => {
              if (!isInteractionLocked) {
                onAnswerSelect(answer);
              }
            }}
            currentOrder={selectedAnswer
              ? (Array.isArray(selectedAnswer) ? selectedAnswer : [])
              : Number(currentQuestion.id) === 9111
                ? [2, 1, 3, 4]
                : []}
            disabled={isInteractionLocked}
            showCorrectAnswer={showExplanation}
            showExplanation={showExplanation}
            isAnswered={!!selectedAnswer}
            theme="premium"
            showProgressIndicator={false}
            explanation={showExplanation ?
              (getBestExplanation(currentQuestion, selectedAnswer) || currentQuestion?.explanation) : undefined}
          />
        )}


        {/* Fallback for unknown question types or legacy questions without question_type */}
        {currentQuestion && (!currentQuestion.question_type || !['mcq_single', 'mcq_multiple', 'true_false', 'fill_blank', 'ordering'].includes(currentQuestion.question_type)) && (
          <MCQSingleQuestionPremium
            question={currentQuestion?.question || ""}
            options={currentQuestion?.options || []}
            selectedAnswer={typeof selectedAnswer === 'string' ? selectedAnswer : undefined}
            showCorrectAnswer={showExplanation}
            onSelectAnswer={(answer: string) => {
              if (!isInteractionLocked) {
                onAnswerSelect(answer);
              }
            }}
            disabled={isInteractionLocked}
            currentIndex={currentIndex}
            totalQuestions={totalQuestions}
            correctAnswer={currentQuestion?.answer || currentQuestion?.correct_answer}
            reference={currentQuestion?.verse_reference || currentQuestion?.format_data?.reference || currentQuestion?.format_data?.verse_reference}
            explanation={showExplanation ?
              (getBestExplanation(currentQuestion, selectedAnswer) || currentQuestion?.explanation) : undefined}
          />
        )}
        <View style={[styles.questionFooterShelf, screenshotMode && styles.questionFooterShelfScreenshot]}>
          <View style={styles.questionFooterControls}>
            <View style={styles.footerTimerRing}>
              <Text style={styles.footerTimerValue}>{displayTimeLeft.toString().padStart(2, '0')}</Text>
              <Text style={styles.footerTimerUnit}>{t('seconds', language)}</Text>
            </View>
            <View style={styles.footerProgressPill}>
              <Text style={styles.footerProgressText}>{currentIndex + 1}/{totalQuestions}</Text>
              <View style={styles.footerProgressTrack}>
                <View
                  style={[
                    styles.footerProgressFill,
                    { width: `${Math.max(8, Math.min(100, ((currentIndex + 1) / Math.max(1, totalQuestions)) * 100))}%` },
                  ]}
                />
              </View>
            </View>
            <TouchableOpacity
              style={[
                styles.footerHintPill,
                hintRevealed && styles.footerHintPillUsed,
                isInteractionLocked && styles.footerHintPillDisabled,
              ]}
              onPress={handleHintPress}
              onTouchEnd={handleHintPress}
              activeOpacity={0.84}
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              accessibilityRole="button"
              accessibilityLabel={hintAccessibilityLabel}
            >
              <Ionicons name="bulb" size={15} color="#FDE68A" />
              <Text style={styles.footerHintText}>{hintButtonLabel}</Text>
              <View style={styles.footerHintCost}>
                <Ionicons name="diamond" size={11} color="#D8B4FE" />
                <Text style={styles.footerHintCostText}>-5</Text>
              </View>
            </TouchableOpacity>
          </View>
          {hintRevealed && (
            <View style={styles.hintRevealBox}>
              <Ionicons name="sparkles" size={14} color="#FDE68A" />
              <Text style={styles.hintRevealText}>{hintText}</Text>
            </View>
          )}
          <Text style={styles.questionMicroInstruction}>
            {questionType === 'mcq_multiple'
              ? t('quizFlow.instructionMcqMultiple', language)
              : questionType === 'true_false'
                ? t('quizFlow.instructionTrueFalse', language)
                : questionType === 'fill_blank'
                  ? t('quizFlow.instructionFillBlank', language)
                  : questionType === 'ordering'
                    ? t('quizFlow.instructionOrdering', language)
                    : t('quizFlow.instructionMcqSingle', language)}
          </Text>
        </View>
        </Animated.View>
        )}

        {showSoloFeedbackStage && currentQuestion && (
          <Animated.View
            testID="iqes-feedback-stage"
            style={[
              styles.feedbackStageCard,
              { minHeight: Math.min(420, Math.max(310, viewportHeight * 0.42)) },
              feedbackStageAnimatedStyle,
            ]}
          >
            <LinearGradient
              colors={feedbackSummary.isCorrect
                ? ['rgba(251, 191, 36, 0.2)', 'rgba(12, 32, 20, 0.0)', 'rgba(34, 197, 94, 0.1)']
                : ['rgba(251, 191, 36, 0.12)', 'rgba(32, 14, 18, 0.0)', 'rgba(248, 113, 113, 0.1)']}
              style={StyleSheet.absoluteFill}
            />
            <View style={styles.celebrationParticles} pointerEvents="none">
              <View style={[styles.rewardSpark, styles.rewardSparkOne]} />
              <View style={[styles.rewardSpark, styles.rewardSparkTwo]} />
              <View style={[styles.rewardSpark, styles.rewardSparkThree]} />
              <View style={[styles.rewardSpark, styles.rewardSparkFour]} />
            </View>
            <Animated.View style={[styles.feedbackBurst, feedbackBurstAnimatedStyle]}>
              <Ionicons
                name={feedbackCopy.icon}
                size={30}
                color="#FDE68A"
              />
            </Animated.View>
            <Text style={styles.stageEyebrow}>{formatLabel}</Text>
            <Text style={[styles.feedbackStageTitle, !feedbackSummary.isCorrect && styles.feedbackStageWrongTitle]}>
              {feedbackCopy.title}
            </Text>
            <Animated.View style={[styles.feedbackDetailsGroup, feedbackDetailsAnimatedStyle]}>
              {!!feedbackSummary.selectedText && (
                <View style={styles.answerRibbon}>
                  <Text style={styles.answerRibbonLabel}>{t('quizFlow.correctAnswer', language)}</Text>
                  <Text style={styles.answerRibbonText}>
                    {feedbackSummary.isCorrect ? feedbackSummary.selectedText : feedbackSummary.correctText || feedbackSummary.selectedText}
                  </Text>
                </View>
              )}
              <View style={styles.xpRewardRow}>
                <View style={styles.xpRewardPill}>
                  <Ionicons name="diamond" size={16} color="#D8B4FE" />
                  <Text style={styles.xpRewardText}>{feedbackSummary.isCorrect ? '+25 XP' : '+0 XP'}</Text>
                </View>
                <View style={styles.xpRewardPill}>
                  <Ionicons name="flame" size={16} color="#FBBF24" />
                  <Text style={styles.xpRewardText}>{t('quizFlow.streak', language)} {Math.max(0, scoreDisplay)}</Text>
                </View>
              </View>
              <TouchableOpacity
                testID="iqes-continue-to-verse"
                style={styles.stagePrimaryButton}
                onPress={() => setSoloRevealStage('verse')}
                activeOpacity={0.86}
              >
                <Text style={styles.stagePrimaryButtonText}>{t('quizFlow.verseReveal', language)}</Text>
                <Ionicons name="arrow-forward" size={18} color="#2B1746" />
              </TouchableOpacity>
              <Text style={styles.feedbackStageBody} numberOfLines={2}>{feedbackCopy.body || explanationText}</Text>
            </Animated.View>
          </Animated.View>
        )}

        {showSoloVerseStage && currentQuestion && (
          <Animated.View
            testID="iqes-verse-stage"
            style={[
              styles.verseStageCard,
              { minHeight: Math.max(560, viewportHeight - Math.max(insets.top, 0) - 130) },
              isCompactVerseViewport && styles.verseStageCardCompact,
              verseStageAnimatedStyle,
            ]}
          >
            <Animated.View
              style={[
                styles.verseScenePlate,
                isCompactVerseViewport && styles.verseScenePlateCompact,
                verseSceneAnimatedStyle,
              ]}
            >
              <IQESSceneBackdrop sceneKey={verseRevealSceneKey} style={styles.verseBackdrop}>
                <LinearGradient
                  colors={['rgba(251, 191, 36, 0.24)', 'rgba(88, 28, 135, 0.18)', 'rgba(8, 11, 30, 0.7)']}
                  style={StyleSheet.absoluteFill}
                />
                <View style={styles.sunGlow} />
                <Ionicons name="book" size={40} color="#FDE68A" />
              </IQESSceneBackdrop>
            </Animated.View>
            <Text style={[styles.stageEyebrow, isCompactVerseViewport && styles.verseStageEyebrowCompact]}>
              {t('quizFlow.verseReveal', language)}
            </Text>
            <Animated.View
              style={[
                styles.verseContentPanel,
                isCompactVerseViewport && styles.verseContentPanelCompact,
                verseTextAnimatedStyle,
              ]}
            >
              <Text style={[styles.verseQuoteMark, isCompactVerseViewport && styles.verseQuoteMarkCompact]}>“</Text>
              {!!verseReference && <Text style={styles.verseStageReference}>{verseReference}</Text>}
              <View style={[styles.insightPanel, isCompactVerseViewport && styles.insightPanelCompact]}>
                <Ionicons name="bulb" size={16} color="#FDE68A" />
                <Text style={styles.insightLabel}>{t('quizFlow.insight', language)}</Text>
              </View>
              <Text style={[styles.verseStageText, isCompactVerseViewport && styles.verseStageTextCompact]}>
                {explanationText}
              </Text>
            </Animated.View>
          </Animated.View>
        )}

        {/* Multiplayer-specific: Quiz content component for complex features */}
        {mode === 'multiplayer' && !isGameFinished && (
          <>
            {/* Handle complex multiplayer features like language mismatch, grace period, etc. */}
            {/* These can be extracted to separate components if needed */}
          </>
        )}
      </ScrollView>

      {/* Next Button - Available for both Solo and Multiplayer modes */}
      {showExplanation && hasSubmittedAnswer && !isGameFinished && onNextQuestion &&
       !(mode === 'multiplayer' && (isWaitingForResults || isSubmittingResults)) && (
        <QuizNextButton
          testID="quiz-next-button"
          // In solo this button carries the Verse Reveal step first, then advances.
          // It used to live inside the retired feedback card; the question now stays
          // on screen behind it, so the reveal is an addition to the answered
          // question rather than a replacement for it.
          onPress={soloVersePending ? () => setSoloRevealStage('verse') : onNextQuestion}
          label={soloVersePending
            ? t('quizFlow.verseReveal', language)
            : (mode === 'solo' ? soloNextLabel : (finalQuestion ? t('finish', language) : t('next_question', language)))}
          hint={mode === 'solo' ? (t('quizFlow.continueWhenReady', language) || "Continue when you're ready") : undefined}
          variant={mode === 'solo' ? 'soft' : 'gradient'}
          style={[
            styles.nextButtonContainer,
            Platform.OS !== 'web' && { paddingBottom: Math.max(spacing.xl, insets.bottom + spacing.lg) },
          ]}
        />
      )}
    </View>
  );
};

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#080B1E',
  },
  scrollContent: {
    paddingBottom: 88,
  },
  scrollContentScreenshot: {
    paddingBottom: 28,
  },
  scrollContentWithStickyCta: {
    paddingBottom: 280,
  },
  scrollContentWithStickyCtaScreenshot: {
    paddingBottom: 210,
  },
  ambientGlowPrimary: {
    position: 'absolute',
    top: -56,
    right: -54,
    width: 128,
    height: 128,
    borderRadius: 64,
    backgroundColor: 'rgba(124, 58, 237, 0.12)',
  },
  ambientGlowGold: {
    position: 'absolute',
    bottom: 68,
    left: -30,
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: 'rgba(245, 158, 11, 0.06)',
  },
  starField: {
    ...StyleSheet.absoluteFillObject,
  },
  star: {
    position: 'absolute',
    width: 3,
    height: 3,
    borderRadius: 2,
    backgroundColor: '#FDE68A',
    opacity: 0.75,
  },
  starOne: {
    top: 118,
    right: 54,
  },
  starTwo: {
    top: 220,
    left: 34,
    opacity: 0.45,
  },
  starThree: {
    bottom: 260,
    right: 86,
    opacity: 0.58,
  },
  starFour: {
    bottom: 170,
    left: 112,
    opacity: 0.5,
  },
  questionRendererContainer: {
    marginVertical: spacing.lg,
    paddingHorizontal: spacing.lg,
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: spacing['2xl'],
  },
  loadingText: {
    marginTop: spacing.lg,
    fontSize: 18,
    color: colors.onSurfaceVariant,
    fontWeight: "600",
    textAlign: "center",
  },
  multiplayerText: {
    fontSize: 16,
    fontWeight: "400",
  },
  errorText: {
    color: colors.onSurface,
    fontSize: 18,
    textAlign: "center",
    fontWeight: "500",
  },
  multiplayerErrorText: {
    color: colors.incorrect,
    fontSize: 16,
    marginBottom: 10,
  },
  retryButton: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    backgroundColor: colors.primary,
    paddingHorizontal: spacing['2xl'],
    paddingVertical: spacing.md,
    borderRadius: radius.full,
    marginTop: spacing.xl,
  },
  retryButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: "bold",
  },
  completionTitle: {
    fontSize: 32,
    color: colors.onSurface,
    fontWeight: "800",
    marginBottom: spacing.lg,
    textAlign: "center",
  },
  scoreText: {
    fontSize: 24,
    color: colors.onSurface,
    fontWeight: "700",
    marginBottom: spacing.sm,
    textAlign: "center",
  },
  congratsText: {
    fontSize: 18,
    color: colors.onSurfaceVariant,
    fontWeight: "500",
    textAlign: "center",
  },
  headerContainer: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
  },
  headerCard: {
    backgroundColor: 'rgba(13, 18, 43, 0.82)',
    borderRadius: 18,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xs,
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.34)',
    shadowColor: '#F59E0B',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.16,
    shadowRadius: 22,
    elevation: 8,
  },
  headerTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  headerMetaGroup: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 0,
  },
  streakHudPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minWidth: 76,
    borderRadius: radius.lg,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    backgroundColor: 'rgba(41, 25, 8, 0.72)',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.28)',
  },
  hudMicroLabel: {
    color: 'rgba(255, 247, 237, 0.68)',
    fontSize: 8,
    lineHeight: 10,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  hudValue: {
    color: '#FDE68A',
    fontSize: 13,
    lineHeight: 16,
    fontWeight: '900',
  },
  categoryHudText: {
    color: '#FDE68A',
    fontSize: 12,
    lineHeight: 15,
    fontWeight: '800',
    textAlign: 'center',
  },
  difficultyHudText: {
    color: 'rgba(255, 247, 237, 0.76)',
    fontSize: 10,
    lineHeight: 12,
    fontWeight: '700',
    textAlign: 'center',
  },
  xpHudPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderRadius: radius.lg,
    paddingHorizontal: spacing.sm,
    paddingVertical: 7,
    backgroundColor: 'rgba(255, 255, 255, 0.07)',
    borderWidth: 1,
    borderColor: 'rgba(216, 180, 254, 0.22)',
  },
  xpHudText: {
    color: '#FFF7ED',
    fontSize: 12,
    lineHeight: 15,
    fontWeight: '900',
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  crownMark: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(245, 158, 11, 0.18)',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.35)',
  },
  brandText: {
    color: '#FDE68A',
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  metaPillRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  headerBadge: {
    alignSelf: 'flex-start',
    backgroundColor: 'rgba(124, 58, 237, 0.28)',
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: radius.full,
    borderWidth: 1,
    borderColor: 'rgba(216, 180, 254, 0.34)',
  },
  headerBadgeText: {
    fontSize: 11,
    lineHeight: 14,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#EDE9FE',
    fontWeight: '700',
  },
  typeBadge: {
    alignSelf: 'flex-start',
    backgroundColor: 'rgba(245, 158, 11, 0.16)',
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: radius.full,
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.34)',
  },
  typeBadgeText: {
    fontSize: 11,
    lineHeight: 14,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: '#FDE68A',
    fontWeight: '800',
  },
  progressText: {
    fontSize: 17,
    color: '#FFF7ED',
    fontWeight: "800",
    lineHeight: 20,
  },
  progressCaption: {
    fontSize: 11,
    color: 'rgba(255, 247, 237, 0.72)',
    fontWeight: '600',
    marginBottom: spacing.xs,
  },
  timerPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.surfaceContainerLow,
    borderRadius: radius.full,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  timerText: {
    fontSize: 14,
    color: colors.primary,
    fontWeight: "700",
  },
  timerRing: {
    width: 46,
    height: 46,
    borderRadius: 23,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(8, 11, 30, 0.78)',
    borderWidth: 2,
    borderColor: '#FBBF24',
    shadowColor: '#F59E0B',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.35,
    shadowRadius: 10,
    elevation: 4,
  },
  timerValue: {
    color: '#FDE68A',
    fontSize: 16,
    lineHeight: 18,
    fontWeight: '900',
  },
  timerUnit: {
    color: 'rgba(255, 247, 237, 0.72)',
    fontSize: 8,
    lineHeight: 10,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  statRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: 0,
    marginBottom: spacing.xs,
  },
  statPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: 'rgba(255, 255, 255, 0.07)',
    borderColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: 1,
    borderRadius: radius.full,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
  },
  statLabel: {
    color: 'rgba(255, 247, 237, 0.7)',
    fontSize: 11,
    fontWeight: '700',
  },
  statValue: {
    color: '#FFF7ED',
    fontSize: 12,
    fontWeight: '900',
  },
  timerBar: {
    marginTop: spacing.xs,
    marginHorizontal: spacing.sm,
    height: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    borderRadius: radius.full,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.16)',
  },
  timerFill: {
    height: '100%',
    borderRadius: radius.full,
  },
  questionFooterShelf: {
    marginHorizontal: spacing.lg,
    marginTop: spacing.md,
    marginBottom: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
    borderRadius: radius.xl,
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.14)',
    backgroundColor: 'rgba(5, 7, 18, 0.2)',
  },
  questionFooterShelfScreenshot: {
    marginTop: spacing.sm,
    marginBottom: spacing.xs,
    paddingTop: spacing.xs,
    paddingBottom: spacing.sm,
    backgroundColor: 'rgba(5, 7, 18, 0.28)',
  },
  questionFooterControls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingHorizontal: spacing.md,
    marginTop: 0,
    marginBottom: spacing.xs,
  },
  footerTimerRing: {
    width: 58,
    height: 58,
    borderRadius: 29,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(8, 11, 30, 0.74)',
    borderWidth: 3,
    borderColor: '#FBBF24',
    shadowColor: '#F59E0B',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.42,
    shadowRadius: 12,
    elevation: 5,
  },
  footerTimerValue: {
    color: '#FDE68A',
    fontSize: 21,
    lineHeight: 23,
    fontWeight: '900',
  },
  footerTimerUnit: {
    color: '#FDE68A',
    fontSize: 8,
    lineHeight: 10,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  footerProgressPill: {
    flex: 1,
    minHeight: 34,
    borderRadius: radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
    backgroundColor: 'rgba(5, 7, 18, 0.68)',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.48)',
  },
  footerProgressText: {
    color: '#FFF7ED',
    fontSize: 13,
    lineHeight: 15,
    fontWeight: '900',
    marginBottom: 3,
  },
  footerProgressTrack: {
    width: '100%',
    height: 4,
    borderRadius: radius.full,
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.16)',
  },
  footerProgressFill: {
    height: '100%',
    borderRadius: radius.full,
    backgroundColor: '#FBBF24',
  },
  footerHintPill: {
    minWidth: 68,
    minHeight: 48,
    borderRadius: radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.sm,
    backgroundColor: 'rgba(88, 28, 135, 0.84)',
    borderWidth: 1,
    borderColor: 'rgba(216, 180, 254, 0.4)',
  },
  footerHintPillUsed: {
    backgroundColor: 'rgba(78, 65, 28, 0.88)',
    borderColor: 'rgba(253, 230, 138, 0.62)',
  },
  footerHintPillDisabled: {
    opacity: 0.62,
  },
  footerHintText: {
    color: '#FFF7ED',
    fontSize: 10,
    lineHeight: 12,
    fontWeight: '900',
  },
  footerHintCost: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  footerHintCostText: {
    color: '#FFF7ED',
    fontSize: 10,
    fontWeight: '900',
  },
  hintRevealBox: {
    marginTop: spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.lg,
    backgroundColor: 'rgba(42, 30, 15, 0.82)',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.44)',
  },
  hintRevealText: {
    flex: 1,
    color: '#FDE68A',
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '700',
  },
  questionMicroInstruction: {
    color: 'rgba(255, 247, 237, 0.86)',
    fontSize: 13,
    lineHeight: 17,
    textAlign: 'center',
    paddingHorizontal: spacing.lg,
    marginBottom: 0,
  },
  questionCard: {
    marginHorizontal: spacing.xl,
    marginBottom: spacing['2xl'],
    backgroundColor: colors.white,
    borderRadius: radius.xl,
    padding: spacing['2xl'],
    ...Platform.select({
      ios: {
        shadowColor: colors.onSurface,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.06,
        shadowRadius: 16,
      },
      android: {
        elevation: 4,
      },
      web: {
        boxShadow: '0 4px 24px rgba(29, 26, 35, 0.06)',
      },
    }),
  },
  questionText: {
    fontSize: 20,
    color: colors.onSurface,
    fontWeight: "700",
    lineHeight: 28,
    textAlign: "center",
  },
  answersContainer: {
    paddingHorizontal: spacing.xl,
    gap: spacing.md,
  },
  answerButton: {
    backgroundColor: colors.white,
    borderRadius: radius.lg,
    padding: spacing.lg,
    borderWidth: 2,
    borderColor: colors.transparent,
    ...Platform.select({
      ios: {
        shadowColor: colors.onSurface,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
      },
      android: {
        elevation: 3,
      },
      web: {
        boxShadow: '0 2px 12px rgba(29, 26, 35, 0.05)',
      },
    }),
  },
  selectedAnswer: {
    backgroundColor: 'rgba(79, 70, 229, 0.1)',
    borderColor: colors.primary,
  },
  correctAnswer: {
    backgroundColor: colors.correctContainer,
    borderColor: colors.correct,
    borderWidth: 3,
  },
  wrongAnswer: {
    backgroundColor: colors.incorrectContainer,
    borderColor: colors.incorrect,
    borderWidth: 3,
  },
  answerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  answerIcon: {
    width: 32,
    height: 32,
    borderRadius: radius.full,
    backgroundColor: colors.surfaceContainerHighest,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  selectedIcon: {
    backgroundColor: colors.primary,
  },
  correctIcon: {
    backgroundColor: colors.correct,
  },
  wrongIcon: {
    backgroundColor: colors.incorrect,
  },
  answerLetter: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.outline,
  },
  selectedLetter: {
    color: colors.onPrimary,
  },
  resultLetter: {
    color: colors.onPrimary,
  },
  resultIcon: {
    position: 'absolute',
  },
  answerText: {
    flex: 1,
    fontSize: 16,
    color: colors.onSurface,
    fontWeight: '500',
    lineHeight: 22,
  },
  selectedAnswerText: {
    color: colors.primary,
    fontWeight: '600',
  },
  resultAnswerText: {
    fontWeight: '600',
  },
  correctAnswerText: {
    color: colors.onCorrect,
    fontWeight: '700',
    ...Platform.select({
      web: {
        textShadow: '0px 1px 2px rgba(29, 26, 35, 0.3)',
      },
      default: {
        textShadowColor: 'rgba(29, 26, 35, 0.3)',
        textShadowOffset: { width: 0, height: 1 },
        textShadowRadius: 2,
      },
    }),
  },
  wrongAnswerText: {
    color: colors.onIncorrect,
    fontWeight: '700',
    ...Platform.select({
      web: {
        textShadow: '0px 1px 2px rgba(29, 26, 35, 0.3)',
      },
      default: {
        textShadowColor: 'rgba(29, 26, 35, 0.3)',
        textShadowOffset: { width: 0, height: 1 },
        textShadowRadius: 2,
      },
    }),
  },
  answerLockCard: {
    marginHorizontal: spacing.lg,
    marginTop: spacing.md,
    marginBottom: spacing.xl,
    padding: spacing.lg,
    borderRadius: 18,
    backgroundColor: 'rgba(12, 10, 28, 0.9)',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.34)',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    shadowColor: '#F59E0B',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.2,
    shadowRadius: 18,
    elevation: 6,
  },
  answerLockIcon: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(245, 158, 11, 0.18)',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.4)',
  },
  answerLockTextGroup: {
    flex: 1,
  },
  answerLockTitle: {
    color: '#FDE68A',
    fontSize: 15,
    lineHeight: 19,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  answerLockBody: {
    color: 'rgba(255, 247, 237, 0.74)',
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '600',
    marginTop: 2,
  },
  answerLockStage: {
    marginHorizontal: spacing.lg,
    marginTop: spacing.xl,
    minHeight: 420,
    borderRadius: 22,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing['2xl'],
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(12, 10, 28, 0.92)',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.36)',
    overflow: 'hidden',
    shadowColor: '#F59E0B',
    shadowOffset: { width: 0, height: 16 },
    shadowOpacity: 0.22,
    shadowRadius: 26,
    elevation: 8,
  },
  lockStageBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  lockCinematicVeil: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(251, 191, 36, 0.2)',
  },
  lockParticleField: {
    ...StyleSheet.absoluteFillObject,
  },
  lockParticle: {
    position: 'absolute',
    width: 5,
    height: 5,
    borderRadius: 3,
    backgroundColor: '#FDE68A',
    opacity: 0.75,
  },
  lockParticleOne: {
    top: 78,
    left: 72,
  },
  lockParticleTwo: {
    top: 138,
    right: 84,
    width: 4,
    height: 4,
  },
  lockParticleThree: {
    bottom: 88,
    left: 120,
    width: 3,
    height: 3,
  },
  lockHalo: {
    width: 88,
    height: 88,
    borderRadius: 44,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.lg,
    backgroundColor: 'rgba(245, 158, 11, 0.18)',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.46)',
  },
  stageEyebrow: {
    color: '#FDE68A',
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  lockStageTitle: {
    color: '#FFF7ED',
    fontSize: 34,
    lineHeight: 40,
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: spacing.md,
  },
  lockStageBody: {
    color: 'rgba(255, 247, 237, 0.78)',
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '600',
    textAlign: 'center',
    maxWidth: 280,
  },
  feedbackStageCard: {
    marginHorizontal: spacing.lg,
    marginTop: spacing.xl,
    marginBottom: spacing.xl,
    minHeight: 0,
    borderRadius: 18,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.lg,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(12, 32, 20, 0.9)',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.4)',
    overflow: 'hidden',
    shadowColor: '#F59E0B',
    shadowOffset: { width: 0, height: 18 },
    shadowOpacity: 0.24,
    shadowRadius: 22,
    elevation: 8,
  },
  celebrationParticles: {
    ...StyleSheet.absoluteFillObject,
  },
  rewardSpark: {
    position: 'absolute',
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#FDE68A',
    opacity: 0.82,
  },
  rewardSparkOne: {
    top: 38,
    left: 46,
  },
  rewardSparkTwo: {
    top: 66,
    right: 58,
    width: 4,
    height: 4,
  },
  rewardSparkThree: {
    bottom: 102,
    left: 74,
    width: 3,
    height: 3,
  },
  rewardSparkFour: {
    bottom: 48,
    right: 94,
    width: 5,
    height: 5,
  },
  feedbackBurst: {
    width: 68,
    height: 68,
    borderRadius: 34,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.sm,
    backgroundColor: 'rgba(245, 158, 11, 0.2)',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.52)',
  },
  feedbackStageTitle: {
    color: '#DCFCE7',
    fontSize: 27,
    lineHeight: 32,
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  feedbackStageWrongTitle: {
    color: '#FCA5A5',
  },
  feedbackDetailsGroup: {
    alignSelf: 'stretch',
    alignItems: 'center',
  },
  answerRibbon: {
    width: '100%',
    borderRadius: 18,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginBottom: spacing.md,
    backgroundColor: 'rgba(251, 191, 36, 0.18)',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.42)',
    alignItems: 'center',
  },
  answerRibbonLabel: {
    color: 'rgba(255, 247, 237, 0.72)',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 3,
  },
  answerRibbonText: {
    color: '#FDE68A',
    fontSize: 28,
    lineHeight: 33,
    fontWeight: '900',
    textAlign: 'center',
  },
  xpRewardRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  xpRewardPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.full,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.12)',
  },
  xpRewardText: {
    color: '#FFF7ED',
    fontSize: 13,
    fontWeight: '800',
  },
  feedbackStageBody: {
    color: 'rgba(255, 247, 237, 0.82)',
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    fontWeight: '600',
    marginTop: spacing.md,
  },
  stagePrimaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    minHeight: 56,
    borderRadius: radius.full,
    paddingHorizontal: spacing.xl,
    alignSelf: 'stretch',
    backgroundColor: '#FDE68A',
  },
  stagePrimaryButtonText: {
    color: '#2B1746',
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '900',
  },
  verseStageCard: {
    marginHorizontal: spacing.lg,
    marginTop: spacing.xl,
    marginBottom: spacing.xl,
    minHeight: 0,
    borderRadius: 24,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(12, 10, 28, 0.93)',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.38)',
    overflow: 'hidden',
    shadowColor: '#F59E0B',
    shadowOffset: { width: 0, height: 18 },
    shadowOpacity: 0.2,
    shadowRadius: 28,
    elevation: 8,
  },
  verseStageCardCompact: {
    marginTop: spacing.md,
    marginBottom: spacing.lg,
    padding: spacing.md,
    borderRadius: 22,
  },
  verseScenePlate: {
    width: '100%',
    height: 132,
    borderRadius: 20,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.32)',
    backgroundColor: '#1F1734',
  },
  verseScenePlateCompact: {
    height: 104,
    marginBottom: spacing.sm,
    borderRadius: 18,
  },
  verseBackdrop: {
    width: '100%',
    height: '100%',
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sunGlow: {
    position: 'absolute',
    top: 16,
    width: 84,
    height: 84,
    borderRadius: 42,
    backgroundColor: 'rgba(251, 191, 36, 0.3)',
  },
  verseContentPanel: {
    alignSelf: 'stretch',
    alignItems: 'center',
    borderRadius: 20,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    paddingBottom: spacing.lg,
    backgroundColor: 'rgba(3, 5, 16, 0.58)',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.22)',
  },
  verseContentPanelCompact: {
    borderRadius: 18,
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
  },
  verseQuoteMark: {
    position: 'absolute',
    top: -14,
    left: 18,
    color: 'rgba(253, 230, 138, 0.18)',
    fontSize: 64,
    lineHeight: 70,
    fontFamily: 'Georgia',
  },
  verseQuoteMarkCompact: {
    top: -10,
    left: 16,
    fontSize: 50,
    lineHeight: 56,
  },
  verseStageEyebrowCompact: {
    marginBottom: spacing.xs,
  },
  verseStageReference: {
    color: '#FDE68A',
    fontSize: 18,
    lineHeight: 24,
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: spacing.md,
  },
  verseStageText: {
    color: '#FFF7ED',
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 0,
    fontFamily: Platform.select({ ios: 'Georgia', android: 'serif', default: 'Georgia' }),
    textShadowColor: 'rgba(0, 0, 0, 0.45)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  verseStageTextCompact: {
    fontSize: 15,
    lineHeight: 22,
  },
  insightPanel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    alignSelf: 'stretch',
    justifyContent: 'center',
    borderRadius: 16,
    paddingVertical: spacing.sm,
    marginBottom: spacing.sm,
    backgroundColor: 'rgba(251, 191, 36, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.24)',
  },
  insightPanelCompact: {
    paddingVertical: 6,
    marginBottom: 6,
    borderRadius: 14,
  },
  insightLabel: {
    color: '#FDE68A',
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  verseStageCaption: {
    color: 'rgba(255, 247, 237, 0.62)',
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '700',
    textAlign: 'center',
  },
  nextButtonContainer: {
    bottom: 0,
    backgroundColor: 'rgba(16, 13, 34, 0.94)',
    paddingTop: spacing.md,
    paddingBottom: spacing.lg,
    borderTopLeftRadius: spacing['2xl'],
    borderTopRightRadius: spacing['2xl'],
    borderTopWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.26)',
    ...Platform.select({
      ios: {
        shadowColor: '#000000',
        shadowOffset: { width: 0, height: -4 },
        shadowOpacity: 0.26,
        shadowRadius: 16,
      },
      android: {
        elevation: 8,
      },
      web: {
        boxShadow: '0 -4px 16px rgba(29, 26, 35, 0.06)',
      },
    }),
  },
  verseRevealCard: {
    marginHorizontal: spacing.lg,
    marginTop: spacing.md,
    marginBottom: spacing['2xl'],
    padding: spacing.lg,
    borderRadius: 18,
    backgroundColor: 'rgba(20, 16, 38, 0.88)',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.32)',
    shadowColor: '#F59E0B',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.12,
    shadowRadius: 18,
    elevation: 5,
  },
  verseRevealHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
    gap: spacing.md,
  },
  verseIcon: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(245, 158, 11, 0.16)',
    borderWidth: 1,
    borderColor: 'rgba(251, 191, 36, 0.34)',
  },
  verseHeaderText: {
    flex: 1,
  },
  verseEyebrow: {
    color: '#FDE68A',
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  verseReference: {
    color: '#FFF7ED',
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '800',
    marginTop: 2,
  },
  verseInsightText: {
    color: '#FFF7ED',
    fontSize: 15,
    lineHeight: 23,
    fontWeight: '500',
  },
});

export default UnifiedQuizRenderer;
